<template>
  <v-container class="publishing-house-page">
    <ApolloQuery
      :query="(gql) => publisherQuery"
      :variables="{ slug: publisherSlug }"
      :update="updatePublisherResult"
      :skip="!publisherSlug"
    >
      <template #default="{ result: { error: pubError, data: publisher }, isLoading: pubLoading }">
        <not-found v-if="isPublisherNotFound(pubError)" />
        <apollo-error-alert
          v-else-if="pubError"
          :error="pubError"
          class="mb-3"
        />

        <div
          v-if="!isPublisherNotFound(pubError)"
          class="publisher-header-divider pb-4 mb-4"
        >
          <v-row no-gutters>
            <v-col
              cols="12"
              sm="auto"
              class="d-flex justify-center justify-sm-start pr-sm-8 pb-3 pb-sm-0"
            >
              <v-skeleton-loader
                v-if="pubLoading && !publisher"
                type="image"
                width="160"
                height="160"
                class="rounded-circle"
              />
              <div
                v-else
                class="publisher-logo"
              >
                <v-img
                  v-if="publisher && publisher.imageUrl"
                  :src="publisher.imageUrl"
                  :alt="publisher.name"
                  height="160"
                  width="160"
                  class="rounded-circle"
                />
              </div>
            </v-col>
            <v-col
              cols="12"
              sm
            >
              <h1 class="book-list-header">
                <span v-if="publisher && publisher.name">{{ publisher.name }}</span>
                <v-skeleton-loader
                  v-else-if="pubLoading"
                  type="heading"
                  width="320"
                />
                <span v-else>{{ $t('routes.publisher') }}</span>
              </h1>

              <div
                v-if="publisherWebsite(publisher)"
                class="d-flex align-center flex-wrap mt-2 publisher-meta"
              >
                <a
                  :href="publisherWebsite(publisher)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="ml-4 text-body-2 publisher-website-link"
                >
                  {{ $t('publishingHouse.websiteLink') }}
                </a>
              </div>

              <p
                v-if="publisher && publisher.description"
                class="mt-3 mb-0 text-body-2 publisher-description"
              >
                {{ publisher.description }}
              </p>

              <div
                v-if="visibleSocialLinks(publisher).length"
                class="d-flex justify-end align-center mt-3 publisher-social"
              >
                <a
                  v-for="social in visibleSocialLinks(publisher)"
                  :key="social.platform"
                  :href="social.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="mx-1 social-link"
                  :aria-label="social.label"
                >
                  <ekitabu-icon
                    size="22"
                    :color="social.color"
                  >
                    {{ social.icon }}
                  </ekitabu-icon>
                </a>
              </div>
            </v-col>
          </v-row>
        </div>

        <v-progress-circular
          v-if="!isPublisherNotFound(pubError) && pubLoading"
          indeterminate
          color="primary"
          class="my-12 d-block mx-auto"
          size="64"
        />

        <ApolloQuery
          v-if="!isPublisherNotFound(pubError) && !pubLoading"
          :query="(gql) => booksQuery"
          :variables="{
            page: currentPage,
            itemsPerPage: pageSize,
            publisherId: (publisher && publisher._id) || null,
            simplesearch: debouncedSearch || null,
            order: orderInput,
          }"
          :update="(data) => data && data.localBooks"
          :skip="!publisher || !publisher._id"
        >
          <template #default="{ result: { error, data }, isLoading, query }">
            <v-row
              no-gutters
              align="center"
              class="mb-3 publishing-house-toolbar"
            >
              <span class="text-body-2 text--secondary results-count">
                {{ resultsLabel(data) }}
              </span>
              <v-spacer />
              <v-text-field
                v-model="searchInput"
                :placeholder="$t('publishingHouse.searchPlaceholder')"
                prepend-inner-icon="mdi-magnify"
                outlined
                tile
                dense
                hide-details
                clearable
                class="publisher-search mr-sm-3"
              />
              <div class="d-flex align-center publisher-sort mt-2 mt-sm-0">
                <span class="mr-2 text-body-2 text--secondary">
                  {{ $t('publishingHouse.sortByLabel') }}
                </span>
                <v-select
                  v-model="sortKey"
                  :items="sortOptions"
                  outlined
                  tile
                  dense
                  hide-details
                  menu-props="offsetY"
                  class="sort-select"
                />
              </div>
            </v-row>

            <book-display
              :data="(data && data.collection) || []"
              :is-loading="!!isLoading"
              :error="error"
              :query="query"
              :loading-items="loadingItemsCount"
              :page-size="pageSize"
              :pagination-enabled="false"
            />

            <v-pagination
              v-if="totalPages(data) > 1"
              v-model="currentPage"
              :length="totalPages(data)"
              :total-visible="7"
              color="primary"
              class="mt-4 publisher-pagination"
              @input="onPageChange"
            />

            <v-container
              v-if="publisher && (!data || !data.collection || data.collection.length === 0) && !isLoading"
            >
              <v-row justify="center">
                <v-col
                  class="col-12 col-sm-8 pa-16"
                  justify="center"
                  align="center"
                >
                  <book-list-icon :size="72" />
                  <h1>{{ $t('shop.noBooksForPublisher') }}</h1>
                  <v-btn
                    tile
                    block
                    color="primary"
                    class="mt-4"
                    :to="{ name: 'home' }"
                  >
                    {{ $t('common.returnToShop') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>
          </template>
        </ApolloQuery>
      </template>
    </ApolloQuery>
  </v-container>
</template>

<script>
import BookListIcon from "@/components/icons/BookListIcon";
import LocalBookFragments from "@/graphql/LocalBookFragments";
import PublisherFragments from "@/graphql/PublisherFragments";
import BookDisplay from "@/components/BookDisplay";
import ApolloErrorAlert from "@/components/ApolloErrorAlert";
import NotFound from "@/views/NotFound.vue";
import gql from "graphql-tag";
import { debounce } from "debounce";
import { buildSocialMeta, PRODUCTION_DOMAIN } from "@/helpers/socialMetaHelper";
import { buildLocalizedPageUrl } from "@/helpers/localeUrlHelper";
import { resolveSocialUrl } from "@/helpers/socialHandleHelper";

const LOADING_ITEM_COUNT = 12;
const PUBLISHER_PAGE_SIZE = 30;
const HTTP_NOT_FOUND = 404;
const PUBLISHER_NOT_FOUND_MESSAGES = Object.freeze([
  "publisherNotFound",
  "publisherSlugRequired",
]);

const SORT_KEYS = Object.freeze({
  NEWEST: "newestToOldest",
  OLDEST: "oldestToNewest",
  PRICE_LOW: "priceLowToHigh",
  PRICE_HIGH: "priceHighToLow",
  TITLE_AZ: "titleAToZ",
  TITLE_ZA: "titleZToA",
});

// Backend OrderFilter on LocalBook exposes: title, price, publisher_name, format_mimeType, publishedOn.
const SORT_TO_ORDER_INPUT = Object.freeze({
  [SORT_KEYS.NEWEST]: [{ publishedOn: "DESC" }],
  [SORT_KEYS.OLDEST]: [{ publishedOn: "ASC" }],
  [SORT_KEYS.PRICE_LOW]: [{ price: "ASC" }],
  [SORT_KEYS.PRICE_HIGH]: [{ price: "DESC" }],
  [SORT_KEYS.TITLE_AZ]: [{ title: "ASC" }],
  [SORT_KEYS.TITLE_ZA]: [{ title: "DESC" }],
});

export default {
  name: "PublisherBookList",
  components: {
    BookListIcon,
    BookDisplay,
    ApolloErrorAlert,
    NotFound,
  },
  metaInfo() {
    const title = this.metaTitle;
    const description = this.$t("seo.publisherDescription");
    return {
      title,
      meta: [
        { vmid: "description", name: "description", content: description },
        ...buildSocialMeta(
          title,
          description,
          `${PRODUCTION_DOMAIN}${buildLocalizedPageUrl(
            this.$route?.path || "/",
            this.$route?.query?.lang,
            this.$store?.getters?.languageCode
          )}`
        ),
      ],
    };
  },
  data() {
    return {
      loadingItemsCount: LOADING_ITEM_COUNT,
      pageSize: PUBLISHER_PAGE_SIZE,
      currentPage: 1,
      searchInput: "",
      debouncedSearch: "",
      sortKey: SORT_KEYS.NEWEST,
      resolvedPublisherName: this.$route?.params?.publisherName || "",
    };
  },
  computed: {
    publisherSlug() {
      return this.$route?.params?.slug || null;
    },
    metaTitle() {
      return this.resolvedPublisherName || this.$t("routes.publisher");
    },
    publisherQuery() {
      return gql`
        query getPublisherBySlug($slug: String!) {
          publisherBySlugPublisher(slug: $slug) {
            ...PublisherDetails
          }
        }
        ${PublisherFragments.publisherDetailsFragment}
      `;
    },
    booksQuery() {
      return gql`
        query getPublisherBooks(
          $page: Int!
          $itemsPerPage: Int
          $publisherId: String
          $simplesearch: String
          $order: [LocalBookFilter_order]
        ) {
          localBooks(
            page: $page
            itemsPerPage: $itemsPerPage
            publisher_id: $publisherId
            simplesearch: $simplesearch
            order: $order
          ) {
            collection {
              ...LocalBookCore
            }
            paginationInfo {
              itemsPerPage
              lastPage
              totalCount
              hasNextPage
            }
          }
        }
        ${LocalBookFragments.localBookCoreFragment}
      `;
    },
    sortOptions() {
      return Object.values(SORT_KEYS).map((key) => ({
        text: this.$t(`publishingHouse.sort.${key}`),
        value: key,
      }));
    },
    orderInput() {
      return SORT_TO_ORDER_INPUT[this.sortKey] || null;
    },
    socialLinks() {
      return [
        { platform: "instagram", handleKey: "instagramHandle", label: "Instagram", icon: "mdi-instagram", color: "#E1306C" },
        { platform: "youtube", handleKey: "youtubeHandle", label: "YouTube", icon: "mdi-youtube", color: "#FF0000" },
        { platform: "facebook", handleKey: "facebookHandle", label: "Facebook", icon: "mdi-facebook", color: "#1877F2" },
        { platform: "tiktok", handleKey: "tiktokHandle", label: "TikTok", icon: "mdi-music-note", color: "#000000" },
        { platform: "twitter", handleKey: "twitterHandle", label: "X", icon: "mdi-twitter", color: "#000000" },
      ];
    },
  },
  watch: {
    searchInput(val) {
      this.debounceSearch(val || "");
    },
    debouncedSearch() {
      this.currentPage = 1;
    },
    sortKey() {
      this.currentPage = 1;
    },
    publisherSlug() {
      this.currentPage = 1;
      this.searchInput = "";
      this.debouncedSearch = "";
      this.resolvedPublisherName = this.$route?.params?.publisherName || "";
    },
  },
  methods: {
    debounceSearch: debounce(function (val) {
      this.debouncedSearch = val.trim();
    }, 300),
    isPublisherNotFound(error) {
      if (!error) return false;

      const graphQLErrors = error.graphQLErrors || [];
      const hasGraphQlNotFound = graphQLErrors.some((graphQLError) => {
        const extensions = graphQLError.extensions || {};
        const status =
          extensions.status ||
          extensions.statusCode ||
          extensions.http?.status ||
          extensions.http?.statusCode ||
          extensions.response?.status ||
          extensions.response?.statusCode;
        return (
          PUBLISHER_NOT_FOUND_MESSAGES.includes(graphQLError.message) ||
          Number(status) === HTTP_NOT_FOUND
        );
      });

      if (hasGraphQlNotFound) return true;

      const networkStatus =
        error.networkError?.status ||
        error.networkError?.statusCode ||
        error.networkError?.response?.status ||
        error.networkError?.response?.statusCode;
      return Number(networkStatus) === HTTP_NOT_FOUND;
    },
    updatePublisherResult(data) {
      const publisher = data?.publisherBySlugPublisher || null;
      this.resolvedPublisherName =
        publisher?.name || this.$route?.params?.publisherName || "";
      return publisher;
    },
    publisherWebsite(publisher) {
      return publisher?.websiteUrl || null;
    },
    visibleSocialLinks(publisher) {
      if (!publisher) return [];
      return this.socialLinks
        .map((social) => ({
          ...social,
          url: resolveSocialUrl(social.platform, publisher[social.handleKey]),
        }))
        .filter((social) => !!social.url);
    },
    resultsLabel(data) {
      const collection = data?.collection || [];
      const total = data?.paginationInfo?.totalCount ?? collection.length;
      const pageStart = (this.currentPage - 1) * this.pageSize;
      const to = pageStart + collection.length;
      const from = collection.length === 0 ? 0 : pageStart + 1;
      return this.$t("publishingHouse.resultsCount", { from, to, total });
    },
    totalPages(data) {
      const total = data?.paginationInfo?.totalCount;
      if (!total) return 0;
      const lastPage = data?.paginationInfo?.lastPage;
      if (lastPage) return lastPage;
      return Math.ceil(total / this.pageSize);
    },
    onPageChange() {
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.publishing-house-page {
  .publisher-header-divider {
    border-bottom: 2px solid var(--v-lightAccent-base);
  }

  .publisher-logo,
  .v-skeleton-loader.rounded-circle {
    height: 160px;
    width: 160px;
    border-radius: 50%;
    background-color: var(--v-lightestAccent-base);
    overflow: hidden;
  }

  .publisher-website-link {
    color: var(--v-info-base, #1976d2);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .publisher-description {
    color: rgba(0, 0, 0, 0.75);
    line-height: 1.5;
  }

  .publisher-social {
    .social-link {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 28px;
      width: 28px;
      border-radius: 50%;
      text-decoration: none;

      &:hover,
      &:focus {
        text-decoration: none;
      }
    }
  }

  .publishing-house-toolbar {
    row-gap: 8px;
  }

  .publisher-search {
    max-width: 380px;
    flex: 1 1 240px;
  }

  .sort-select {
    min-width: 160px;
    max-width: 220px;
  }

  .results-count {
    white-space: nowrap;
  }
}

@media (max-width: 599px) {
  .publishing-house-page {
    .publisher-search {
      max-width: none;
      flex-basis: 100%;
    }

    .publisher-sort {
      width: 100%;
      justify-content: space-between;
    }
  }
}
</style>
